/**
 * Outil « Gaz et pression » (Boîte à outils, physique-chimie 1re et Tle) — logique PURE.
 *
 * Deux parties :
 * - Seringue : de l'air enfermé (quantité n fixe). En 1re, loi de Mariotte à température
 *   constante (P × V = constante, P en fonction de V ou de 1/V). En Tle, gaz parfait
 *   P × V = n × R × T : transformations isotherme, isochore, isobare ; température absolue ;
 *   volume molaire, masse volumique du gaz.
 * - Profondeur (1re) : pression dans un liquide au repos, P = P_atm + ρ × g × h ; force pressante
 *   F = ΔP × S sur le tympan d'un plongeur.
 *
 * Unités internes SI (Pa, m³, K) ; affichage en hPa, bar, mL, °C.
 */

import { frNum, mcqOptions, pick, retryMcq, type Mcq, type Rng } from '../../ui/mcq';

export const fr = frNum;
const NB = String.fromCharCode(0xa0);

export const R = 8.314; // J/(mol·K)
export const P_ATM = 101_300; // Pa
export const G = 9.81; // N/kg
export const T0 = 273.15;
export const M_AIR = 29; // g/mol

export type Part = 'syringe' | 'depth';

export const PARTS: ReadonlyArray<{ id: Part; label: string }> = [
  { id: 'syringe', label: 'Seringue' },
  { id: 'depth', label: 'Profondeur' },
];

/** Tle (ou sans niveau) : gaz parfait ; 1re : Mariotte et profondeur */
export const idealGas = (rank: number | null) => rank === null || rank >= 7;

export function partsFor(rank: number | null) {
  return PARTS.filter((p) => p.id !== 'depth' || rank === null || rank <= 6);
}

/* ---------- seringue ---------- */

/** air de la seringue : 50 mL à 1 013 hPa et 20 °C */
export const V0_ML = 50;
export const THETA0 = 20;
export const N_AIR = (P_ATM * V0_ML * 1e-6) / (R * (THETA0 + T0));

export type Mode = 'isotherm' | 'isochor' | 'isobar';

export const MODES: ReadonlyArray<{ id: Mode; label: string; fixed: string }> = [
  { id: 'isotherm', label: 'Isotherme', fixed: 'T constante' },
  { id: 'isochor', label: 'Isochore', fixed: 'V constant' },
  { id: 'isobar', label: 'Isobare', fixed: 'P constante' },
];

export const V_RANGE: readonly [number, number] = [10, 60]; // mL
export const THETA_RANGE: readonly [number, number] = [-50, 150]; // °C

export interface GasState {
  /** Pa */
  P: number;
  /** mL */
  V: number;
  /** K */
  T: number;
  /** mol */
  n: number;
}

/** état de l'air selon la transformation : `V` (mL) en isotherme, `theta` (°C) sinon */
export function gasState(mode: Mode, V: number, theta: number): GasState {
  const n = N_AIR;
  if (mode === 'isotherm') {
    const T = THETA0 + T0;
    return { n, V, T, P: (n * R * T) / (V * 1e-6) };
  }
  const T = theta + T0;
  if (mode === 'isochor') return { n, V: V0_ML, T, P: (n * R * T) / (V0_ML * 1e-6) };
  return { n, T, P: P_ATM, V: ((n * R * T) / P_ATM) * 1e6 };
}

export const hPa = (P: number) => `${fr(P / 100, 0)}${NB}hPa`;
export const bar = (P: number) => `${fr(P / 1e5, 2)}${NB}bar`;
export const kelvin = (T: number) => `${fr(T, 0)}${NB}K`;
export const celsius = (T: number) => `${fr(T - T0, 0)}${NB}°C`;

/** volume molaire (L/mol) et masse volumique de l'air (g/L) */
export const molarVolume = (s: GasState) => s.V / 1000 / s.n;
export const airDensity = (s: GasState) => (s.P * M_AIR) / (R * s.T) / 1000;

export function syringeLines(mode: Mode, s: GasState, rank: number | null): string[] {
  const out: string[] = [];
  if (!idealGas(rank)) {
    out.push(
      `Loi de Mariotte (température constante) : P × V = ${hPa(s.P)} × ${fr(s.V, 0)}${NB}mL = ${fr((s.P / 100) * s.V, 0)}${NB}hPa·mL, toujours la même valeur.`,
    );
    out.push(
      s.V < V0_ML
        ? `Volume divisé par ${fr(V0_ML / s.V, 2)} : pression multipliée par ${fr(V0_ML / s.V, 2)}.`
        : s.V > V0_ML
          ? `Volume multiplié par ${fr(s.V / V0_ML, 2)} : pression divisée par ${fr(s.V / V0_ML, 2)}.`
          : 'Seringue à 50 mL, à la pression atmosphérique.',
    );
    out.push(
      'Échelle microscopique : comprimé, le gaz a plus de particules par unité de volume, donc plus de chocs sur la paroi : la pression augmente.',
    );
    return out;
  }
  out.push(
    `P × V = n × R × T : ${fr(s.n * 1000, 2)} × 10⁻³${NB}mol × 8,314 × ${kelvin(s.T)} = ${fr(s.n * R * s.T, 2)}${NB}J, et P × V = ${fr(s.P * s.V * 1e-6, 2)}${NB}J.`,
  );
  if (mode === 'isotherm')
    out.push(
      'Isotherme : T constante, donc P × V constant : P est inversement proportionnelle à V.',
    );
  if (mode === 'isochor')
    out.push(
      `Isochore : V constant, donc P ÷ T constant = ${fr(s.P / s.T, 1)}${NB}Pa/K ; P est proportionnelle à la température absolue T, pas à θ en °C.`,
    );
  if (mode === 'isobar')
    out.push(
      `Isobare : P constante, donc V ÷ T constant = ${fr(s.V / s.T, 3)}${NB}mL/K ; prolongée, la droite passe par 0 K = −273,15 °C (zéro absolu).`,
    );
  out.push(
    `T (K) = θ (°C) + 273,15 : ${celsius(s.T)} ↔ ${kelvin(s.T)}. Plus T est grande, plus l’agitation des particules est forte.`,
  );
  return out;
}

/* ---------- profondeur ---------- */

export const LIQUIDS: ReadonlyArray<{ id: string; label: string; rho: number }> = [
  { id: 'fresh', label: 'Eau douce', rho: 1000 },
  { id: 'sea', label: 'Eau de mer', rho: 1025 },
];

/** surface du tympan (m²) : 0,5 cm² */
export const EARDRUM = 0.5e-4;

export function depthPressure(rho: number, h: number): number {
  return P_ATM + rho * G * h;
}

export function depthLines(rho: number, h: number, equalized: boolean): string[] {
  const dP = rho * G * h;
  const out = [
    `P = P_atm + ρ × g × h = ${hPa(P_ATM)} + ${fr(rho, 0)} × 9,81 × ${fr(h, 0)} = ${hPa(P_ATM + dP)} (${bar(P_ATM + dP)}).`,
    'Loi de la statique des fluides : P_B − P_A = ρ × g × (z_A − z_B) ; environ 1 bar de plus tous les 10 m d’eau.',
  ];
  if (h === 0) out.push('En surface, seule la pression atmosphérique s’exerce.');
  else if (equalized)
    out.push(
      'Oreille équilibrée : l’air derrière le tympan est à la même pression, la force pressante sur le tympan se compense.',
    );
  else
    out.push(
      `Oreille non équilibrée : F = ΔP × S = ${fr(dP, 0)}${NB}Pa × 0,5${NB}cm² = ${fr(dP * EARDRUM, 1)}${NB}N sur le tympan (le poids d’environ ${fr(((dP * EARDRUM) / G) * 1000, 0)}${NB}g).`,
    );
  return out;
}

/* ---------- défi ---------- */

export type QuizKind =
  | 'mariotte'
  | 'force'
  | 'units'
  | 'depth'
  | 'micro'
  | 'kelvin'
  | 'ideal-gas'
  | 'isochor'
  | 'isobar'
  | 'molar-volume';

export type Quiz = Mcq<QuizKind>;

export function quizKinds(rank: number | null): QuizKind[] {
  if (rank === null) return ['mariotte', 'depth', 'kelvin', 'ideal-gas', 'isochor'];
  if (idealGas(rank)) return ['kelvin', 'ideal-gas', 'isochor', 'isobar', 'molar-volume'];
  return ['mariotte', 'force', 'units', 'depth', 'micro'];
}

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  return retryMcq((r) => buildQuiz(kind, r), rng);
}

type W = readonly [string, string];
/** une décimale, gardée (« 2,0 ») */
const f1 = (v: number) => v.toFixed(1).replace('.', ',');
const q = (
  kind: QuizKind,
  question: string,
  good: string,
  wrongs: W[],
  explain: string,
  rng: Rng,
): Quiz => ({ kind, question, options: mcqOptions(good, wrongs, rng), explain });

function buildQuiz(kind: QuizKind, rng: Rng): Quiz {
  switch (kind) {
    case 'mariotte': {
      const [V1, V2] = pick(
        [
          [60, 20],
          [50, 25],
          [40, 10],
          [30, 60],
        ] as const,
        rng,
      );
      const P1 = 1000;
      const P2 = (P1 * V1) / V2;
      return q(
        kind,
        `Une seringue bouchée contient ${V1}${NB}mL d’air à ${fr(P1, 0)}${NB}hPa. À température constante, on amène le volume à ${V2}${NB}mL. Nouvelle pression ?`,
        `${fr(P2, 0)}${NB}hPa`,
        [
          [
            `${fr((P1 * V2) / V1, 0)}${NB}hPa`,
            'P × V reste constant : si V diminue, P augmente (et inversement).',
          ],
          [`${fr(P1, 0)}${NB}hPa`, 'Le volume change : la pression change aussi.'],
          [`${fr(P1 + (V1 - V2), 0)}${NB}hPa`, 'Ce n’est pas une différence : P₁ × V₁ = P₂ × V₂.'],
        ],
        `Loi de Mariotte : P₂ = P₁ × V₁ ÷ V₂ = ${fr(P1, 0)} × ${V1} ÷ ${V2} = ${fr(P2, 0)}${NB}hPa.`,
        rng,
      );
    }
    case 'force': {
      const P = pick([2, 3, 5], rng); // bar
      const S = pick([10, 20, 50], rng); // cm²
      const F = P * 1e5 * S * 1e-4;
      return q(
        kind,
        `Un gaz à ${P}${NB}bar presse sur un piston de ${S}${NB}cm². Valeur de la force pressante ?`,
        `${fr(F, 0)}${NB}N`,
        [
          [`${fr(P * S, 0)}${NB}N`, 'Convertis d’abord : 1 bar = 10⁵ Pa et 1 cm² = 10⁻⁴ m².'],
          [`${fr((P * 1e5) / (S * 1e-4), 0)}${NB}N`, 'F = P × S, on multiplie.'],
          [`${fr(F * 100, 0)}${NB}N`, '1 cm² = 10⁻⁴ m² (et non 10⁻² m²).'],
        ],
        `F = P × S = ${P} × 10⁵${NB}Pa × ${S} × 10⁻⁴${NB}m² = ${fr(F, 0)}${NB}N.`,
        rng,
      );
    }
    case 'units': {
      const v = pick([1013, 2500, 850], rng);
      return q(
        kind,
        `${fr(v, 0)}${NB}hPa, c’est…`,
        `${fr(v * 100, 0)}${NB}Pa`,
        [
          [`${fr(v / 100, 2)}${NB}Pa`, 'hecto = 100 : 1 hPa = 100 Pa, on multiplie.'],
          [`${fr(v, 0)}${NB}bar`, '1 bar = 1 000 hPa : ce serait énorme.'],
          [`${fr(v * 1000, 0)}${NB}Pa`, '1 hPa = 100 Pa, pas 1 000.'],
        ],
        `1${NB}hPa = 100${NB}Pa : ${fr(v, 0)}${NB}hPa = ${fr(v * 100, 0)}${NB}Pa = ${fr(v / 1000, 3)}${NB}bar.`,
        rng,
      );
    }
    case 'depth': {
      const h = pick([10, 20, 30], rng);
      const P = depthPressure(1000, h);
      return q(
        kind,
        `Pression dans l’eau (ρ = 1${NB}000${NB}kg/m³) à ${h}${NB}m de profondeur ? (P_atm ≈ 1,0 bar ; g = 9,8 N/kg)`,
        `${f1(P / 1e5)}${NB}bar`,
        [
          [
            `${f1((1000 * G * h) / 1e5)}${NB}bar`,
            'Il faut ajouter la pression atmosphérique à la surface.',
          ],
          [`${f1(h)}${NB}bar`, 'Environ 1 bar de plus tous les 10 m, pas par mètre.'],
          ['1,0 bar', 'La pression augmente avec la profondeur.'],
          [
            `${f1((2 * P) / 1e5)}${NB}bar`,
            'La pression ne double pas : elle gagne environ 1 bar tous les 10 m.',
          ],
        ],
        `P = P_atm + ρ × g × h ≈ 1,0 + ${f1((1000 * G * h) / 1e5)} = ${f1(P / 1e5)}${NB}bar.`,
        rng,
      );
    }
    case 'micro': {
      const hot = rng() < 0.5;
      return q(
        kind,
        hot
          ? 'On chauffe un gaz enfermé dans un récipient rigide. À l’échelle microscopique, que se passe-t-il ?'
          : 'On comprime un gaz à température constante. Pourquoi sa pression augmente-t-elle ?',
        hot
          ? 'Les particules s’agitent plus vite : chocs plus nombreux et plus violents sur la paroi'
          : 'Les particules sont plus serrées : il y a plus de chocs sur la paroi',
        [
          ['Les particules grossissent', 'La taille des particules ne change pas.'],
          [
            'Le nombre de particules augmente',
            'Le récipient est fermé : le nombre de particules ne change pas.',
          ],
          hot
            ? [
                'Les particules sont plus serrées',
                'Le récipient est rigide : le volume, donc la place des particules, ne change pas.',
              ]
            : [
                'Les particules s’agitent plus vite',
                'À température constante, l’agitation ne change pas.',
              ],
        ],
        hot
          ? 'La température mesure l’agitation des particules ; la pression vient de leurs chocs sur la paroi.'
          : 'Même agitation, mais plus de particules par unité de volume : plus de chocs, pression plus grande.',
        rng,
      );
    }
    case 'kelvin': {
      const th = pick([20, -10, 100, 37], rng);
      return q(
        kind,
        `${th}${NB}°C en kelvins ?`,
        `${fr(th + T0, 2)}${NB}K`,
        [
          [`${fr(th - T0, 2)}${NB}K`, 'On ajoute 273,15 : T (K) = θ (°C) + 273,15.'],
          [`${fr(th, 0)}${NB}K`, 'Les deux échelles ne partent pas du même zéro.'],
          [`${fr(-th + T0, 2)}${NB}K`, 'Attention au signe de θ.'],
        ],
        `T = θ + 273,15 = ${fr(th + T0, 2)}${NB}K ; une température absolue est toujours positive.`,
        rng,
      );
    }
    case 'ideal-gas': {
      const V = pick([2, 5, 10], rng); // L
      const P = 1e5;
      const T = 300;
      const n = (P * V * 1e-3) / (R * T);
      return q(
        kind,
        `${V}${NB}L de gaz parfait à 1,0 × 10⁵${NB}Pa et 300${NB}K (R = 8,314 J·mol⁻¹·K⁻¹). Quantité de matière n ?`,
        `${fr(n, 2)}${NB}mol`,
        [
          [
            `${fr((P * V) / (R * T), 0)}${NB}mol`,
            `Convertis le volume en m³ : ${V} L = ${V} × 10⁻³ m³.`,
          ],
          [`${fr((R * T) / (P * V * 1e-3), 1)}${NB}mol`, 'n = P × V ÷ (R × T), pas l’inverse.'],
          [
            `${fr((P * V * 1e-3) / (R * (T - T0)), 2)}${NB}mol`,
            'T est déjà en kelvins : n’enlève pas 273.',
          ],
        ],
        `n = P × V ÷ (R × T) = 1,0 × 10⁵ × ${V} × 10⁻³ ÷ (8,314 × 300) ≈ ${fr(n, 2)}${NB}mol.`,
        rng,
      );
    }
    case 'isochor': {
      const [t1, t2] = pick(
        [
          [27, 127],
          [0, 100],
          [27, 327],
        ] as const,
        rng,
      );
      const P1 = 1.0;
      const P2 = (P1 * (t2 + T0)) / (t1 + T0);
      return q(
        kind,
        `Un gaz dans un récipient rigide est à ${fr(P1, 1)}${NB}bar et ${t1}${NB}°C. On le chauffe à ${t2}${NB}°C. Nouvelle pression ?`,
        `${fr(P2, 2)}${NB}bar`,
        [
          [
            t1 === 0 ? `${fr(P1 * 2, 2)}${NB}bar` : `${fr((P1 * t2) / t1, 2)}${NB}bar`,
            'P est proportionnelle à la température ABSOLUE : convertis en kelvins.',
          ],
          [`${fr(P1, 2)}${NB}bar`, 'Le volume est constant, pas la pression.'],
          [
            `${fr((P1 * (t1 + T0)) / (t2 + T0), 2)}${NB}bar`,
            'Chauffer à volume constant augmente la pression.',
          ],
        ],
        `Isochore : P ÷ T constant, P₂ = P₁ × T₂ ÷ T₁ = ${fr(P1, 1)} × ${fr(t2 + T0, 0)} ÷ ${fr(t1 + T0, 0)} ≈ ${fr(P2, 2)}${NB}bar.`,
        rng,
      );
    }
    case 'isobar': {
      const V1 = pick([1.0, 2.0], rng);
      const [t1, t2] = pick(
        [
          [27, 87],
          [0, 273],
          [20, 80],
        ] as const,
        rng,
      );
      const V2 = (V1 * (t2 + T0)) / (t1 + T0);
      return q(
        kind,
        `Un ballon contient ${fr(V1, 1)}${NB}L de gaz à ${t1}${NB}°C. À pression constante, on le porte à ${t2}${NB}°C. Nouveau volume ?`,
        `${fr(V2, 2)}${NB}L`,
        [
          [
            t1 === 0 ? `${fr(V1 * 10, 2)}${NB}L` : `${fr((V1 * t2) / t1, 2)}${NB}L`,
            'V est proportionnel à la température ABSOLUE : convertis en kelvins.',
          ],
          [`${fr(V1, 2)}${NB}L`, 'C’est la pression qui est constante, pas le volume.'],
          [
            `${fr((V1 * (t1 + T0)) / (t2 + T0), 2)}${NB}L`,
            'Chauffer à pression constante augmente le volume.',
          ],
        ],
        `Isobare : V ÷ T constant, V₂ = V₁ × T₂ ÷ T₁ = ${fr(V1, 1)} × ${fr(t2 + T0, 0)} ÷ ${fr(t1 + T0, 0)} ≈ ${fr(V2, 2)}${NB}L.`,
        rng,
      );
    }
    case 'molar-volume': {
      const th = pick([0, 20, 25], rng);
      const Vm = ((R * (th + T0)) / P_ATM) * 1000;
      return q(
        kind,
        `Volume molaire d’un gaz parfait à ${th}${NB}°C sous 1${NB}013${NB}hPa ?`,
        `${fr(Vm, 1)}${NB}L/mol`,
        [
          [`${fr(((R * th) / P_ATM) * 1000, 2)}${NB}L/mol`, 'T en kelvins : T = θ + 273,15.'],
          [
            `${fr(Vm * 1000, 0)}${NB}L/mol`,
            `V_m = R × T ÷ P donne des m³/mol : ${fr(Vm / 1000, 4)} m³/mol = ${fr(Vm, 1)} L/mol.`,
          ],
          [`${fr(P_ATM / (R * (th + T0)), 1)}${NB}L/mol`, 'V_m = R × T ÷ P, pas P ÷ (R × T).'],
        ],
        `V_m = R × T ÷ P = 8,314 × ${fr(th + T0, 2)} ÷ 101${NB}300 ≈ ${fr(Vm, 1)}${NB}L/mol : le même pour tous les gaz parfaits.`,
        rng,
      );
    }
  }
}
